import secureLocalStorage from "react-secure-storage";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import LoginService from "../../../Services/LoginService";
import DataParser from "../../../utlits/Dataparser";
import "../../../auth-modern.css";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // 🔹 Load saved credentials and Auto-redirect if already logged in
  useEffect(() => {
    // Park any chat invite token from the URL so it survives the login flow.
    // The backend may generate links pointing to the root (/), so we park here too.
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get("chat_invite");
    if (inviteToken) {
      localStorage.setItem("planit_pending_chat_invite", inviteToken);
      window.history.replaceState({}, "", window.location.pathname);
    }

    const savedEmail = localStorage.getItem("rememberEmail");
    const savedPassword = localStorage.getItem("rememberPassword");
    const savedRemember = localStorage.getItem("rememberMe");

    if (savedRemember === "true" && savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const loginData = { email, password };

    LoginService.loginAdmin(loginData)
      .then((res) => {
        if (!res.Good) {
          setMessage(res.data);
          setLoading(false);
          return;
        }

        if (res.isDefaultPassword) {
          navigate("/initial-reset-password", { state: { email: res.userEmail } });
          setLoading(false);
          return;
        }

        secureLocalStorage.setItem("adminInfo", JSON.stringify(res));
        secureLocalStorage.setItem("admins", res.authToken);
        secureLocalStorage.setItem("loginTime", JSON.stringify(new Date().getTime()));
        secureLocalStorage.setItem("sessionTimeoutMins", JSON.stringify(res.sessionTimeoutMins || 360));

        if (rememberMe) {
          localStorage.setItem("rememberEmail", email);
          localStorage.setItem("rememberPassword", password);
          localStorage.setItem("rememberMe", "true");
        } else {
          localStorage.removeItem("rememberEmail");
          localStorage.removeItem("rememberPassword");
          localStorage.removeItem("rememberMe");
        }

        const role = res?.user?.userdata?.role_title || "";
        const policyAssigned = res?.user?.company?.policy;
        const userPermissions = res?.user?.userdata?.user_permissions || [];

        if (role?.toLowerCase() !== "company admin" && userPermissions.length === 0) {
          setMessage("No permission assigned to you. Please contact company admin or hr to add permissions");
          secureLocalStorage.removeItem("adminInfo");
          secureLocalStorage.removeItem("admins");
          setLoading(false);
          return;
        }

        if (role?.toLowerCase() !== "company admin" && !policyAssigned) {
          setMessage("No policy is assigned to your account. Please contact your HR or Administrator.");
          secureLocalStorage.removeItem("adminInfo");
          secureLocalStorage.removeItem("admins");
          setLoading(false);
          return;
        }

        const navigateTo = (path) => {
          // Trigger state refresh in App.js to pick up new login info
          window.dispatchEvent(new Event("permissionsUpdated"));
          
          // 🔹 Small delay to ensure state sync before mounting protected routes
          setTimeout(() => {
            navigate(path);
          }, 50);
        };

        const company = res.company_id;
        if (company === 0) {
          navigateTo(`/${DataParser.CompanyIdentifierRole()}/admin-dashboard`);
        } else {
          const roleLower = role?.toLowerCase();
          const companyRole = DataParser.CompanyIdentifierRole();

          if (roleLower === "hr") {
            navigateTo(`/${companyRole}/dashboard`);
          } else if (roleLower === "company admin") {
            navigateTo(`/${companyRole}/employee-attendance`);
          } else {
            const hasSeenWelcome = res?.user?.userdata?.has_seen_welcome;
            if (hasSeenWelcome === true) {
              if (userPermissions.includes("myattendance-list")) {
                navigateTo(`/${companyRole}/employee-attendance`);
              } else if (userPermissions.includes("tasks-list")) {
                navigateTo(`/${companyRole}/tasks`);
              } else if (userPermissions.includes("myleave-list")) {
                navigateTo(`/${companyRole}/employee_leaves`);
              } else if (userPermissions.includes("myevaluation-list")) {
                navigateTo(`/${companyRole}/myevaluation`);
              } else {
                navigateTo(`/${companyRole}/employee-attendance`);
              }
            } else {
              navigateTo("/home");
            }
          }
        }
      })
      .catch((err) => {
        setMessage(err.response?.data?.message || err.message);
        setLoading(false);
      });
  };

  return (
    <>
      <Helmet>
        <title>Login - Planit</title>
      </Helmet>
      
      <div className="auth-modern-wrapper">
        <div className="auth-modern-card">
          <div className="auth-logo-container">
            <Link to="/">
              <img src="/assets/images/logo-dark.png" alt="logo" height={32} />
            </Link>
          </div>
          
          <h2 className="auth-welcome-text">Welcome back!</h2>

          {message && <div className="auth-error-alert">{message}</div>}

          <form onSubmit={handleSubmit}>
            <div className="auth-input-group">
              <input
                type="email"
                className="auth-input"
                placeholder=" "
                id="useremail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <label className="auth-label" htmlFor="useremail">Email</label>
            </div>

            <div className="auth-input-group">
              <input
                type={showPassword ? "text" : "password"}
                className="auth-input"
                placeholder=" "
                id="userpassword"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <label className="auth-label" htmlFor="userpassword">Password</label>
              <button
                type="button"
                className={`auth-password-toggle${password ? ' auth-password-filled' : ''}`}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <div className="auth-password-icon-container">
                  <img src="/assets/images/password.svg" alt="" />
                  {!showPassword && <div className="auth-password-slash"></div>}
                </div>
              </button>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="auth-remember-check"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="auth-remember-check" style={{ fontSize: '13px' }}>
                  Remember me
                </label>
              </div>
              <Link to="/forgot-password" title="Forgot Password" style={{ fontSize: '13px', color: '#74788d', textDecoration: 'none' }}>
                Forgot Password?
              </Link>
            </div>

            {!loading ? (
              <button className="auth-primary-btn" type="submit">Log In</button>
            ) : (
              <button className="auth-primary-btn" disabled>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Logging In...
              </button>
            )}
          </form>

          <p className="auth-footer-text">
            © {new Date().getFullYear()} Planit. Developed by <a href="https://websouls.com/" target="_blank" rel="noopener noreferrer" className="auth-link">WebSouls</a>
          </p>
          
        </div>

        <div className="auth-bottom-help">
          <Link to="/">Need help?</Link>
        </div>
      </div>
    </>
  );
};

export default Login;
